import { useState } from "react";
import type { GeneratedDefinition, GeneratedSection } from "@keydials/api-client";
import { SectionColourPicker, type BuilderSelection, type ResolvedSelection, type SegmentNoun } from "@keydials/dial-workspace";
import { useFeedbackDock } from "../components/chrome/FeedbackDock";
import type { BuildAction } from "./builderReducer";
import { ChecklistItems } from "./ChecklistItems";
import { ConfirmRemove } from "./ConfirmRemove";
import { builderCopy as copy, fill, itemCount, nounValues } from "./copy";

export interface SelectionPanelProps {
  model: GeneratedDefinition;
  resolved: ResolvedSelection | null;
  noun: SegmentNoun;
  /** True once the design has moved on (out for checking, or live) and can't be edited here. */
  disabled: boolean;
  dispatch: (action: BuildAction) => void;
  onSelect: (selection: BuilderSelection | null) => void;
}

/**
 * The panel beside the wheel (KEY-560): it edits whatever the wheel has selected. A section's name, what it's about and its action
 * points; an action point's name, and its checklist on a dial with one. Removing anything asks first, in place.
 */
export function SelectionPanel({ model, resolved, noun, disabled, dispatch, onSelect }: SelectionPanelProps) {
  const dock = useFeedbackDock();
  const words = nounValues(noun);
  const [confirming, setConfirming] = useState<string | null>(null);
  const itemised = model.kind === "Itemised";

  const section = resolved ? model.sections[resolved.sectionIndex] : null;
  const segment = section && resolved?.segmentIndex !== undefined ? section.segments[resolved.segmentIndex] : null;

  const escapeHatch = (
    <button type="button" className="kd-builder-link kd-panel-escape" onClick={dock.open}>
      {copy.build.escapeHatch}
    </button>
  );

  if (!section || !resolved) {
    return (
      <aside className="kd-selection-panel" aria-label={copy.build.helpSectionTitle}>
        <Help title={copy.build.helpSectionTitle} body={copy.build.helpSectionBody} />
        {escapeHatch}
      </aside>
    );
  }

  const sectionKey = section.key;

  if (segment) {
    const segmentIndex = resolved.segmentIndex!;
    const armed = `segment:${sectionKey}/${segment.key}`;
    return (
      <aside className="kd-selection-panel" aria-label={segment.name || copy.build.untitled}>
        <button type="button" className="kd-builder-link" onClick={() => onSelect({ level: "section", sectionKey })}>
          {section.label || copy.build.untitled}
        </button>
        <div className="kd-panel-eyebrow">{fill(copy.build.segmentEyebrow, { ...words, n: segmentIndex + 1, count: section.segments.length })}</div>
        <label className="kd-builder-field">
          <span>{copy.build.nameLabel}</span>
          <input
            value={segment.name}
            disabled={disabled}
            autoFocus={!segment.name}
            onChange={(e) => dispatch({ type: "renameSegment", sectionKey, segmentKey: segment.key, name: e.target.value })}
          />
        </label>
        <div className="kd-panel-actions">
          <button type="button" className="kd-icon-btn" disabled={disabled || segmentIndex === 0} onClick={() => dispatch({ type: "moveSegment", sectionKey, segmentKey: segment.key, delta: -1 })}>
            {copy.build.moveUp}
          </button>
          <button
            type="button"
            className="kd-icon-btn"
            disabled={disabled || segmentIndex === section.segments.length - 1}
            onClick={() => dispatch({ type: "moveSegment", sectionKey, segmentKey: segment.key, delta: 1 })}
          >
            {copy.build.moveDown}
          </button>
          <button type="button" className="kd-icon-btn" disabled={disabled} onClick={() => setConfirming(armed)}>
            {copy.build.remove}
          </button>
        </div>
        {confirming === armed && (
          <ConfirmRemove
            name={segment.name}
            onKeep={() => setConfirming(null)}
            onRemove={() => {
              setConfirming(null);
              dispatch({ type: "removeSegment", sectionKey, segmentKey: segment.key });
            }}
          />
        )}
        {itemised && (
          <ChecklistItems
            sectionKey={sectionKey}
            segment={segment}
            noun={noun}
            selectedItemKey={resolved.selection.level === "item" ? resolved.selection.itemKey : null}
            disabled={disabled}
            confirming={confirming}
            setConfirming={setConfirming}
            dispatch={dispatch}
          />
        )}
        <Help title={fill(copy.build.helpSegmentTitle, words)} body={copy.build.helpSegmentBody} />
        {escapeHatch}
      </aside>
    );
  }

  const armedSection = `section:${sectionKey}`;
  return (
    <aside className="kd-selection-panel" aria-label={section.label || copy.build.untitled}>
      <div className="kd-panel-eyebrow">{fill(copy.build.sectionEyebrow, { n: resolved.sectionIndex + 1, count: model.sections.length })}</div>
      <label className="kd-builder-field">
        <span>{copy.build.nameLabel}</span>
        <input
          value={section.label}
          disabled={disabled}
          autoFocus={!section.label}
          onChange={(e) => dispatch({ type: "renameSection", sectionKey, label: e.target.value })}
        />
      </label>
      <label className="kd-builder-field">
        <span>
          {copy.build.aboutLabel} <em className="kd-panel-optional">{copy.build.optional}</em>
        </span>
        <input
          value={section.description ?? ""}
          disabled={disabled}
          placeholder={copy.build.aboutPlaceholder}
          onChange={(e) => dispatch({ type: "describeSection", sectionKey, description: e.target.value })}
        />
      </label>

      {/* KEY-755: the band's own colour on the dial. */}
      <SectionColourPicker
        label={copy.build.colourLabel}
        value={section.colour}
        disabled={disabled}
        onChange={(colour) => dispatch({ type: "colourSection", sectionKey, colour })}
      />

      <h3 className="kd-panel-heading">{fill(copy.build.listHeading, { ...words, n: section.segments.length })}</h3>
      <SegmentList
        section={section}
        itemised={itemised}
        disabled={disabled}
        confirming={confirming}
        setConfirming={setConfirming}
        dispatch={dispatch}
        onSelect={onSelect}
      />
      <div className="kd-panel-actions">
        <button type="button" className="kd-btn" disabled={disabled} onClick={() => dispatch({ type: "addSegment", sectionKey })}>
          {fill(copy.build.addSegment, words)}
        </button>
      </div>

      <div className="kd-panel-actions kd-panel-section-actions">
        <button type="button" className="kd-icon-btn" disabled={disabled || resolved.sectionIndex === 0} onClick={() => dispatch({ type: "moveSection", sectionKey, delta: -1 })}>
          {copy.build.moveUp}
        </button>
        <button
          type="button"
          className="kd-icon-btn"
          disabled={disabled || resolved.sectionIndex === model.sections.length - 1}
          onClick={() => dispatch({ type: "moveSection", sectionKey, delta: 1 })}
        >
          {copy.build.moveDown}
        </button>
        <button type="button" className="kd-icon-btn" disabled={disabled} onClick={() => setConfirming(armedSection)}>
          {copy.build.remove}
        </button>
      </div>
      {confirming === armedSection && (
        <ConfirmRemove
          name={section.label}
          onKeep={() => setConfirming(null)}
          onRemove={() => {
            setConfirming(null);
            dispatch({ type: "removeSection", sectionKey });
          }}
        />
      )}

      <Help title={copy.build.helpSectionTitle} body={copy.build.helpSectionBody} />
      {escapeHatch}
    </aside>
  );
}

function SegmentList({
  section,
  itemised,
  disabled,
  confirming,
  setConfirming,
  dispatch,
  onSelect,
}: {
  section: GeneratedSection;
  itemised: boolean;
  disabled: boolean;
  confirming: string | null;
  setConfirming: (key: string | null) => void;
  dispatch: (action: BuildAction) => void;
  onSelect: (selection: BuilderSelection) => void;
}) {
  if (section.segments.length === 0) return null;
  return (
    <ul className="kd-panel-list">
      {section.segments.map((segment, index) => {
        const armed = `segment:${section.key}/${segment.key}`;
        return (
          <li key={segment.key} className="kd-panel-row-wrap">
            <div className="kd-panel-row">
              <button type="button" className="kd-panel-row-name" onClick={() => onSelect({ level: "segment", sectionKey: section.key, segmentKey: segment.key })}>
                {segment.name || copy.build.untitled}
                {itemised && <span className="kd-panel-row-meta">{itemCount(segment.items.length)}</span>}
              </button>
              <button
                type="button"
                className="kd-icon-btn"
                disabled={disabled || index === 0}
                onClick={() => dispatch({ type: "moveSegment", sectionKey: section.key, segmentKey: segment.key, delta: -1 })}
              >
                {copy.build.moveUp}
              </button>
              <button
                type="button"
                className="kd-icon-btn"
                disabled={disabled || index === section.segments.length - 1}
                onClick={() => dispatch({ type: "moveSegment", sectionKey: section.key, segmentKey: segment.key, delta: 1 })}
              >
                {copy.build.moveDown}
              </button>
              <button type="button" className="kd-icon-btn" disabled={disabled} onClick={() => setConfirming(armed)}>
                {copy.build.remove}
              </button>
            </div>
            {confirming === armed && (
              <ConfirmRemove
                name={segment.name}
                onKeep={() => setConfirming(null)}
                onRemove={() => {
                  setConfirming(null);
                  dispatch({ type: "removeSegment", sectionKey: section.key, segmentKey: segment.key });
                }}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

function Help({ title, body }: { title: string; body: string }) {
  return (
    <div className="kd-panel-help">
      <b>{title}</b>
      {body}
    </div>
  );
}
